import { ChevronDown, ChevronRight, ExternalLink, FileText, Link as LinkIcon } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { useCustomerContracts } from '@/hooks/useContracts';
import { formatCurrency, formatDate } from '@/lib/format';
import { invoiceNinjaRecurringUrl } from '@/lib/integrations';
import type { Contract, ContractLineItem } from '@/types/api';

/**
 * Vertrags-Sub-Tab pro Kunde (WT-203, Sprint 2).
 *
 * Zeigt für jeden mit dem Kunden verknüpften Recurring-Invoice:
 *   - Vertrags-Header (Number, Status, Frequenz, Betrag, nächste Send-Date)
 *   - Service-Profil aggregiert nach line_items[].category
 *   - Line-Items als Collapsible mit notes + Custom-Values
 *
 * Kein Vertrag = EmptyState mit Tiefenlink zum Mapping-Wizard.
 */

interface Props {
  customerId: string;
}

export function ContractsTab({ customerId }: Props): JSX.Element {
  const { data, isLoading, isError, error } = useCustomerContracts(customerId);

  if (isLoading) return <SkeletonRows rows={3} cols={4} />;
  if (isError) return <ErrorAlert error={error} />;

  const contracts = data?.data ?? [];

  if (contracts.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<FileText className="size-8" />}
          title="Kein verknüpfter Vertrag"
          description="Dieser Kunde hat aktuell keinen aktiven Invoice-Ninja-Recurring-Invoice zugeordnet. Mappe einen bestehenden IN-Vertrag, oder lege einen IN-seitig an und triggere danach den Sync."
          actions={
            <Link
              to="/settings/contracts/mapping"
              search={{ customer: customerId }}
            >
              <Button leftIcon={<LinkIcon className="size-4" />}>Vertrag zuordnen</Button>
            </Link>
          }
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {contracts.map((c) => (
        <ContractCard key={c.id} contract={c} />
      ))}
    </div>
  );
}

function ContractCard({ contract }: { contract: Contract }): JSX.Element {
  const services = aggregateServices(contract.line_items ?? []);
  const items = contract.line_items ?? [];

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <span>Vertrag {contract.number ?? '–'}</span>
          <StatusBadge status={contract.status} />
        </span>
      }
      description={
        <>
          IN-ID: <span className="font-mono">{contract.invoice_ninja_id}</span> ·{' '}
          synchronisiert{' '}
          <span className="font-mono">{formatDate(contract.synced_at.split('T')[0] ?? null)}</span>
        </>
      }
      actions={
        <a
          href={invoiceNinjaRecurringUrl(contract.invoice_ninja_id)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          In Invoice Ninja öffnen
          <ExternalLink className="size-3" aria-hidden="true" />
        </a>
      }
    >
      <dl className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted">Frequenz</dt>
          <dd className="font-medium">{contract.frequency ?? 'unbekannt'}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted">Nächster Versand</dt>
          <dd className="font-medium">{formatDate(contract.next_send_date)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted">Betrag</dt>
          <dd className="font-medium">{formatCurrency(contract.amount ?? null)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted">Line-Items</dt>
          <dd className="font-medium">{items.length}</dd>
        </div>
      </dl>

      <div className="mt-4 border-t border-border pt-3">
        <h3 className="mb-2 text-sm font-semibold">Service-Profil</h3>
        {services.length === 0 ? (
          <p className="text-xs text-muted">
            Keine Kategorien (custom_value2) in den Line-Items - vermutlich kein Wartungsbezug.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {services.map((s) => (
              <Badge key={s.category} tone="primary">
                {s.category} × {s.count}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <details className="mt-3 border-t border-border pt-3">
          <summary className="flex cursor-pointer items-center gap-1 text-sm text-muted hover:text-text">
            <ChevronDown className="size-4 group-open:hidden" aria-hidden="true" />
            <ChevronRight className="size-4 hidden group-open:block" aria-hidden="true" />
            Line-Items im Detail ({items.length})
          </summary>
          <ul className="mt-2 flex flex-col gap-2">
            {items.map((it, i) => (
              <LineItemRow key={`${contract.id}-${i}`} item={it} />
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

function LineItemRow({ item }: { item: ContractLineItem }): JSX.Element {
  return (
    <li className="rounded-md border border-border p-2 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">{item.product_key ?? '(ohne Produktschlüssel)'}</span>
        <span className="font-mono text-xs text-muted">
          {item.quantity ?? 1} × {formatCurrency(item.cost ?? null)} ={' '}
          {formatCurrency(item.line_total ?? null)}
        </span>
      </div>
      {(item.category || item.unit) && (
        <p className="mt-1 flex flex-wrap gap-2 text-xs">
          {item.category && (
            <Badge tone="primary" className="text-[0.7rem]">
              {item.category}
            </Badge>
          )}
          {item.unit && <span className="text-muted">Einheit: {item.unit}</span>}
        </p>
      )}
      {item.notes && (
        <p className="mt-1 whitespace-pre-wrap text-xs text-muted">{item.notes}</p>
      )}
    </li>
  );
}

function StatusBadge({ status }: { status: Contract['status'] }): JSX.Element {
  switch (status) {
    case 'active':
      return <Badge tone="success">aktiv</Badge>;
    case 'paused':
      return <Badge tone="warning">pausiert</Badge>;
    case 'expired':
      return <Badge tone="neutral">abgelaufen</Badge>;
    case 'deleted':
      return <Badge tone="danger">geloescht</Badge>;
    default:
      return <Badge tone="neutral">unbekannt</Badge>;
  }
}

interface ServiceAgg {
  category: string;
  count: number;
}

function aggregateServices(items: ContractLineItem[]): ServiceAgg[] {
  const counts = new Map<string, number>();
  for (const it of items) {
    const cat = it.category?.trim();
    if (!cat) continue;
    counts.set(cat, (counts.get(cat) ?? 0) + (it.quantity ?? 1));
  }
  return Array.from(counts.entries())
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count);
}
